import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Package, Plus, Trash2, Upload, Star, ShoppingCart, Tag, ImageIcon, DollarSign, Package2 } from "lucide-react";
import { useForm, router, usePage } from "@inertiajs/react";
import { Product, Category, Brand } from "@/types";
import imageCompression from "browser-image-compression";

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  categories: Category[];
  brands: Brand[];
}

const ProductFormModal = ({
  isOpen,
  onClose,
  product,
  categories,
  brands,
}: ProductFormModalProps) => {
  const { data, setData, errors, reset, processing } = useForm({
    product: {
      name: "",
      description: "",
      long_description: "",
      youtube_video_url: "",
      is_featured: false,
      price: "",
      market_price: "",
      stock: "",
      category_id: "",
      brand_id: "",
      sku: "",
      specifications: [] as { name: string; value: string }[],
      cart_enabled: true,
      images: [] as File[],
       removed_images: [] as number[],
      product_images_positions: {} as {[key: string]: number},
      discount_percentage: "",
      warranty_duration: "",
      warranty_coverage: "",
      support: "",
      returns_policy: "",
      exclusive_features: [{ icon: "", title: "", description: "" }],
      exclusive_reviews: [{ comment: "", person: "", profession: "" }],
      faqs: [{ question: "", answer: "" }],
    },
  });

  const { props } = usePage();
  const flashErrors = (props as any).flash?.errors || {};
  const directErrors = (props as any).errors || {};

  // Estado para controlar si se deben mostrar errores del flash
  const [showFlashErrors, setShowFlashErrors] = React.useState(true);
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  // Combinar errores de useForm con errores del flash y directos
  const allErrors = {
    ...errors,
    ...(hasSubmitted ? directErrors : {}),
    ...(showFlashErrors ? flashErrors : {}),
  };
  const generalErrorMessages = Object.entries(allErrors as Record<string, string>)
    .map(([, message]) => message)
    .filter(Boolean);

  const [existingImages, setExistingImages] = React.useState<{id: number, image_url: string, position: number}[]>([]);
  const [imagePositions, setImagePositions] = React.useState<number[]>([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const [imageLimitWarning, setImageLimitWarning] = useState<string>("");

  // Estado para campos exclusivos
  const [exclusiveFeatures] = useState([
    { icon: 'Star', title: '', description: '' },
    { icon: 'Shield', title: '', description: '' },
    { icon: 'Zap', title: '', description: '' }
  ]);
  const [exclusiveReviews, setExclusiveReviews] = useState([
    { comment: '', person: '', profession: '' }
  ]);
  const [faqs, setFaqs] = useState([
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' }
  ]);

  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (product) {
        setShowFlashErrors(true);
        setHasSubmitted(false);
        setData("product", {
          name: product.name,
          description: product.description,
          long_description: product.long_description || "",
          youtube_video_url: product.youtube_video_url || "",
          is_featured: product.type === "FeaturedProduct",
          price: String(product.price),
          market_price: String(product.market_price || ""),
          stock: String(product.stock),
          category_id: String(product.category_id),
          brand_id: String(product.brand_id),
          images: [],
          sku: product.sku,
          specifications: product.specifications || [],
          removed_images: [],
          cart_enabled: product.cart_enabled,
          product_images_positions: {},
          discount_percentage: "",
          warranty_duration: product.warranty_duration || "",
          warranty_coverage: product.warranty_coverage || "",
          support: product.support || "",
          returns_policy: product.returns_policy || "",
          exclusive_features: product.exclusive_features ? JSON.parse(product.exclusive_features) : [{ icon: "", title: "", description: "" }],
          exclusive_reviews: product.exclusive_reviews ? JSON.parse(product.exclusive_reviews) : [{ comment: "", person: "", profession: "" }],
          faqs: product.faqs ? JSON.parse(product.faqs) : [{ question: "", answer: "" }],
        });
        setExistingImages(product.product_images || []);
      } else {
        setShowFlashErrors(false);
        setHasSubmitted(false);
        reset();
        setExistingImages([]);
      }
    }
  }, [product, isOpen]);

  const handleSpecChange = (
    index: number,
    field: "name" | "value",
    value: string,
  ) => {
    const newSpecs = [...data.product.specifications];
    newSpecs[index] = { ...newSpecs[index], [field]: value };
    setData("product.specifications", newSpecs);
  };

  const addSpec = () => {
    setData("product.specifications", [
      ...data.product.specifications,
      { name: "", value: "" },
    ]);
  };

  const removeSpec = (index: number) => {
    const newSpecs = data.product.specifications.filter((_, i) => i !== index);
    setData("product.specifications", newSpecs);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!product && existingImages.length + data.product.images.length === 0) {
      setImageLimitWarning("Selecciona al menos una imagen antes de crear el producto.");
      return;
    }

    setImageLimitWarning("");

    const url = product ? `/admin/products/${product.id}` : "/admin/products";

    // Prepare image positions data
    const imagePositionsData: {[key: string]: number} = {};

    // Add positions for existing images
    existingImages.forEach((img) => {
      imagePositionsData[img.id] = img.position;
    });

    // Add positions for new images (they will be handled on the backend)
    // The backend will assign positions based on the order

     const dataToSubmit = {
       name: data.product.name.trim(),
       description: data.product.description.trim(),
       long_description: data.product.long_description.trim(),
       youtube_video_url: data.product.youtube_video_url.trim(),
       sku: data.product.sku.trim(),
       price: data.product.price,
       market_price: data.product.market_price,
       stock: data.product.stock,
       category_id: data.product.category_id,
       brand_id: data.product.brand_id,
       cart_enabled: data.product.cart_enabled,
       is_featured: data.product.is_featured,
       specifications: JSON.stringify(data.product.specifications),
       product_images_positions: imagePositionsData,
       product_images_attributes: [
         ...existingImages.map(img => ({ id: img.id, _destroy: false })),
         ...data.product.removed_images.map(id => ({ id, _destroy: true }))
       ],
       images: data.product.images,
       exclusive_features: JSON.stringify(exclusiveFeatures),
       exclusive_reviews: JSON.stringify(exclusiveReviews),
       faqs: JSON.stringify(faqs),
       warranty_duration: data.product.warranty_duration,
       warranty_coverage: data.product.warranty_coverage,
       support: data.product.support,
       returns_policy: data.product.returns_policy,
     };

    if (product) {
      router.put(
        url,
        { product: dataToSubmit },
        {
          forceFormData: true,
          onSuccess: () => {
            onClose();
          },
          onError: (errors) => {
            console.log("🔴 Errores de validación en actualización:", errors);
          },
          preserveState: true,
        },
      );
    } else {
      router.post(
        url,
        { product: dataToSubmit },
        {
          forceFormData: true,
          onSuccess: () => {
            onClose();
          },
          onError: (errors) => {
            console.log("🔴 Errores de validación en creación:", errors);
          },
          preserveState: true,
        },
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
        <DialogHeader className="space-y-4 pb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Package className="h-7 w-7 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-3xl font-bold">
                {product ? "Editar Producto" : "Nuevo Producto"}
              </DialogTitle>
              <DialogDescription className="text-lg">
                {product
                  ? "Modifica los datos del producto en tu catálogo."
                  : "Agrega un nuevo producto a tu catálogo de la tienda."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-10">
          {generalErrorMessages.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              <p className="font-semibold">No se pudo guardar el producto</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                {generalErrorMessages.map((message, index) => (
                  <li key={`${message}-${index}`}>{message}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Información Básica */}
          <Card className="border-blue-200 dark:border-blue-800">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Tag className="h-6 w-6 text-blue-600" />
                Información Básica
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">
                    Nombre del Producto *
                  </Label>
                  <Input
                    id="name"
                    value={data.product.name}
                    onChange={(e) => setData("product.name", e.target.value)}
                    placeholder="iPhone 15 Pro Max"
                  />
                  {allErrors["product.name"] && (
                    <p className="text-red-500 text-sm">
                      {allErrors["product.name"].split(" ").slice(1).join(" ")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sku" className="text-sm font-medium">
                    SKU *
                  </Label>
                  <Input
                    id="sku"
                    value={data.product.sku}
                    onChange={(e) => setData("product.sku", e.target.value)}
                    placeholder="IPH15PM-256"
                  />
                  {allErrors["product.sku"] && (
                    <p className="text-red-500 text-sm">
                      {allErrors["product.sku"].split(" ").slice(1).join(" ")}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <Label htmlFor="brand-form" className="text-sm font-medium">
                    Marca *
                  </Label>
                  <Select
                    onValueChange={(value) =>
                      setData("product.brand_id", value)
                    }
                    value={data.product.brand_id}
                  >
                    <SelectTrigger id="brand-form" className="h-11 border border-gray-300">
                      <SelectValue placeholder="Seleccionar marca" />
                    </SelectTrigger>
                      <SelectContent>
                        {brands.map((brand) => (
                          <SelectItem key={brand.id} value={brand.id.toString()}>
                            {brand.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                   </Select>
                  {allErrors["product.brand_id"] && (
                    <p className="text-red-500 text-sm">
                      {allErrors["product.brand_id"]
                        .split(" ")
                        .slice(1)
                        .join(" ")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category-form" className="text-sm font-medium">
                    Categoría *
                  </Label>
                  <Select
                    onValueChange={(value) =>
                      setData("product.category_id", value)
                    }
                    value={data.product.category_id}
                  >
                    <SelectTrigger id="category-form" className="h-11 border border-gray-300">
                      <SelectValue placeholder="Seleccionar categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem
                          key={category.id}
                          value={String(category.id)}
                        >
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {allErrors["product.category_id"] && (
                    <p className="text-red-500 text-sm">
                      {allErrors["product.category_id"]
                        .split(" ")
                        .slice(1)
                        .join(" ")}
                    </p>
                  )}
                </div>
              </div>

               <div className="space-y-2">
                 <Label htmlFor="description" className="text-sm font-medium">
                   Descripción *
                 </Label>
                  <Textarea
                    id="description"
                    value={data.product.description}
                    onChange={(e) =>
                      setData("product.description", e.target.value)
                    }
                    placeholder="Descripción atractiva que destaque las características principales del producto"
                    rows={4}
                    className="resize-none"
                  />
                 {allErrors["product.description"] && (
                   <p className="text-red-500 text-sm">
                     {allErrors["product.description"]
                       .split(" ")
                       .slice(1)
                       .join(" ")}
                   </p>
                 )}
               </div>

               <div className="space-y-2">
                 <Label htmlFor="youtube_video_url" className="text-sm font-medium">
                   URL del Video de YouTube (opcional)
                 </Label>
                  <Input
                    id="youtube_video_url"
                    value={data.product.youtube_video_url}
                    onChange={(e) =>
                      setData("product.youtube_video_url", e.target.value)
                    }
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
               </div>
            </CardContent>
          </Card>

          {/* Precios e Inventario */}
          <Card className="border-green-200 dark:border-green-800">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-2 text-xl">
                <DollarSign className="h-6 w-6 text-green-600" />
                Precios e Inventario
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="space-y-2">
                  <Label htmlFor="price" className="text-sm font-medium">
                    Precio de Venta (S/) *
                  </Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={data.product.price}
                    onChange={(e) => setData("product.price", e.target.value)}
                    placeholder="1199.99"
                    className="h-11"
                  />
                  {allErrors["product.price"] && (
                    <p className="text-red-500 text-sm">
                      {allErrors["product.price"].split(" ").slice(1).join(" ")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="market_price" className="text-sm font-medium">
                    Precio de Mercado (S/)
                  </Label>
                  <Input
                    id="market_price"
                    type="number"
                    step="0.01"
                    value={data.product.market_price}
                    onChange={(e) =>
                      setData("product.market_price", e.target.value)
                    }
                    placeholder="1299.99"
                    className="h-11"
                  />
                  <p className="text-xs text-muted-foreground">
                    Precio tachado para mostrar descuento
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stock" className="text-sm font-medium">
                    Stock *
                  </Label>
                  <Input
                    id="stock"
                    type="number"
                    value={data.product.stock}
                    onChange={(e) => setData("product.stock", e.target.value)}
                    placeholder="25"
                    className="h-11"
                  />
                  {allErrors["product.stock"] && (
                    <p className="text-red-500 text-sm">
                      {allErrors["product.stock"].split(" ").slice(1).join(" ")}
                    </p>
                  )}
                </div>
              </div>

              {/* Switches mejorados */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-6 border-t">
                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <div className="flex items-center gap-3">
                    <ShoppingCart className="h-5 w-5 text-blue-600" />
                    <div>
                      <Label htmlFor="cart_enabled" className="text-sm font-medium cursor-pointer">
                        Habilitar Carrito de Compras
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Permite compra directa del producto
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="cart_enabled"
                    checked={data.product.cart_enabled}
                    onCheckedChange={(checked) =>
                      setData("product.cart_enabled", checked)
                    }
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Star className="h-5 w-5 text-yellow-600" />
                    <div>
                      <Label htmlFor="is_featured" className="text-sm font-medium cursor-pointer">
                        Producto Exclusivo
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Destaca en sección especial
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="is_featured"
                    checked={data.product.is_featured}
                    onCheckedChange={(checked) =>
                      setData("product.is_featured", checked)
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Especificaciones */}
          <Card className="border-purple-200 dark:border-purple-800">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Package2 className="h-6 w-6 text-purple-600" />
                Especificaciones Técnicas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
               {data.product.specifications.map((spec, index) =>
                 <div key={index} className="flex items-center gap-3 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
                   <Input
                     value={spec.name || ""}
                     onChange={(e) =>
                       handleSpecChange(index, "name", e.target.value)
                     }
                     placeholder="Ej: Memoria RAM"
                     className="flex-1"
                   />
                   <Input
                     value={spec.value || ""}
                     onChange={(e) =>
                       handleSpecChange(index, "value", e.target.value)
                     }
                     placeholder="Ej: 8GB"
                     className="flex-1"
                   />
                   <Button
                     type="button"
                     variant="destructive"
                     size="sm"
                     onClick={() => removeSpec(index)}
                   >
                     <Trash2 className="h-4 w-4" />
                   </Button>
                 </div>
               )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSpec}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Añadir Especificación
              </Button>
            </CardContent>
          </Card>

          {/* Multimedia */}
          <Card className="border-orange-200 dark:border-orange-800">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-2 text-xl">
                <ImageIcon className="h-6 w-6 text-orange-600" />
                Imágenes del Producto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="images" className="text-sm font-medium">
                  Subir Imágenes (máximo 4)
                </Label>
                <div className="border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg p-6 text-center hover:border-primary transition-colors">
                  <Upload className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                   <Input
                     ref={imageInputRef}
                     id="images"
                     name="product[images][]"
                     type="file"
                     multiple
                     accept="image/*"
                     disabled={isCompressing}
                     className="sr-only"
                     onChange={async (e) => {
                       if (e.target.files) {
                         const selectedFiles = Array.from(e.target.files);
                         const totalImages = existingImages.length + data.product.images.length + selectedFiles.length;

                         if (totalImages > 4) {
                           setImageLimitWarning("No se puede subir más de 4 imágenes por producto. Solo se agregarán las primeras imágenes válidas.");
                         } else {
                           setImageLimitWarning("");
                         }

                         setIsCompressing(true);
                         try {
                           const options = {
                             maxSizeMB: 1,
                             maxWidthOrHeight: 1920,
                             useWebWorker: true,
                           };
                           const compressedFiles = await Promise.all(
                             selectedFiles.map((file) =>
                               imageCompression(file, options),
                             ),
                           );
                            const combined = [
                              ...data.product.images,
                              ...compressedFiles,
                            ].slice(0, 4 - existingImages.length);
                            setData("product.images", combined);

                            // Set default positions for new images
                            const currentMaxPosition = Math.max(
                              ...existingImages.map(img => img.position),
                              ...imagePositions,
                              existingImages.length
                            ) || 0;
                            const newPositions = compressedFiles.slice(0, 4 - existingImages.length - data.product.images.length).map((_, index) =>
                              currentMaxPosition + index + 1
                            );
                            setImagePositions([...imagePositions, ...newPositions]);
                         } catch (error) {
                           console.error("Error compressing images:", error);
                            const newFiles = selectedFiles.slice(0, 4 - existingImages.length - data.product.images.length);
                            const combined = [
                              ...data.product.images,
                              ...newFiles,
                            ];
                            setData("product.images", combined);

                            // Set default positions for new images
                            const currentMaxPosition = Math.max(
                              ...existingImages.map(img => img.position),
                              ...imagePositions,
                              existingImages.length
                            ) || 0;
                            const newPositions = newFiles.map((_, index) =>
                              currentMaxPosition + index + 1
                            );
                            setImagePositions([...imagePositions, ...newPositions]);
                         } finally {
                           setIsCompressing(false);
                           if (imageInputRef.current) {
                             imageInputRef.current.value = "";
                           }
                         }
                       }
                     }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={isCompressing}
                    className="mt-2"
                  >
                    {isCompressing ? "Procesando..." : "Seleccionar Imágenes"}
                  </Button>
                    <p className="text-sm text-muted-foreground mt-2">
                      PNG, JPG o WebP hasta 1MB cada una
                    </p>
                    {imageLimitWarning && (
                      <p className="text-orange-500 text-sm mt-2">{imageLimitWarning}</p>
                    )}
                    {hasSubmitted && !product && existingImages.length + data.product.images.length === 0 && (
                      <p className="text-red-500 text-sm mt-2">Debe agregar al menos una imagen</p>
                    )}
                 </div>
                
                 {/* Vista previa de imágenes */}
                 {(existingImages.length > 0 || data.product.images.length > 0) && (
                   <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                     {existingImages.map((image, index) => (
                       <div key={`existing-${index}`} className="relative group border rounded-lg p-4">
                         <img
                           src={image.image_url}
                           alt={`Imagen ${index + 1}`}
                           className="w-full h-32 object-cover rounded-lg border mb-3"
                         />
                         <div className="flex items-center gap-2 mb-2">
                           <Label htmlFor={`position-existing-${index}`} className="text-sm font-medium">
                             Posición:
                           </Label>
                           <Input
                             id={`position-existing-${index}`}
                             type="number"
                             min="1"
                             value={image.position}
                             onChange={(e) => {
                               const newPosition = parseInt(e.target.value) || 1;
                               const updatedImages = existingImages.map((img, i) =>
                                 i === index ? { ...img, position: newPosition } : img
                               );

                               // Check for duplicate positions
                               const otherPositions = updatedImages.filter((_, i) => i !== index).map(img => img.position);
                               if (otherPositions.includes(newPosition)) {
                                 // Could show an error here, but for now just allow it
                                 // The backend validation will catch it
                               }

                               setExistingImages(updatedImages);
                               setData("product.product_images_positions", {
                                 ...data.product.product_images_positions,
                                 [image.id]: newPosition,
                               });
                             }}
                             className="w-20 h-8 text-center"
                           />
                         </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newExistingImages = existingImages.filter(
                                (_, i) => i !== index,
                              );
                              setExistingImages(newExistingImages);
                              setData("product.removed_images", [
                                ...data.product.removed_images,
                                image.id,
                              ]);
                              // Remove position from product_images_positions
                              const newPositions = { ...data.product.product_images_positions };
                              delete newPositions[image.id];
                              setData("product.product_images_positions", newPositions);
                            }}
                           className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                         >
                           ×
                         </button>
                       </div>
                     ))}
                     {data.product.images.map((file, index) => (
                       <div key={`new-${index}`} className="relative group border rounded-lg p-4">
                         <img
                           src={URL.createObjectURL(file)}
                           alt={`Nueva imagen ${index + 1}`}
                           className="w-full h-32 object-cover rounded-lg border mb-3"
                         />
                         <div className="flex items-center gap-2 mb-2">
                           <Label htmlFor={`position-new-${index}`} className="text-sm font-medium">
                             Posición:
                           </Label>
                           <Input
                             id={`position-new-${index}`}
                             type="number"
                             min="1"
                             value={imagePositions[index] || (existingImages.length + index + 1)}
                             onChange={(e) => {
                               const newPosition = parseInt(e.target.value) || 1;
                               const newImagePositions = [...imagePositions];
                               newImagePositions[index] = newPosition;
                               setImagePositions(newImagePositions);
                             }}
                             className="w-20 h-8 text-center"
                           />
                         </div>
                         <button
                           type="button"
                           onClick={() => {
                             const newImages = data.product.images.filter(
                               (_, i) => i !== index,
                             );
                             setData("product.images", newImages);
                             const newImagePositions = imagePositions.filter(
                               (_, i) => i !== index,
                             );
                             setImagePositions(newImagePositions);
                           }}
                           className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                         >
                           ×
                         </button>
                       </div>
                     ))}
                   </div>
                 )}
                {allErrors["product.images"] && (
                  <p className="text-red-500 text-sm">
                    {allErrors["product.images"].split(" ").slice(1).join(" ")}
                  </p>
                )}
              </div>
            </CardContent>
           </Card>

           {/* Garantía y Soporte */}
           <Card className="border-indigo-200 dark:border-indigo-800">
             <CardHeader className="pb-6">
               <CardTitle className="flex items-center gap-2 text-xl">
                 <Package className="h-6 w-6 text-indigo-600" />
                 Garantía y Soporte
               </CardTitle>
             </CardHeader>
             <CardContent className="space-y-8">
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                 <div className="space-y-2">
                   <Label htmlFor="warranty_duration" className="text-sm font-medium">
                     Duración de Garantía
                   </Label>
                   <Input
                     id="warranty_duration"
                     value={data.product.warranty_duration}
                     onChange={(e) => setData("product.warranty_duration", e.target.value)}
                     placeholder="Ej: 2 años"
                     className="h-11"
                   />
                 </div>
                 <div className="space-y-2">
                   <Label htmlFor="support" className="text-sm font-medium">
                     Soporte Técnico
                   </Label>
                   <Input
                     id="support"
                     value={data.product.support}
                     onChange={(e) => setData("product.support", e.target.value)}
                     placeholder="Ej: Soporte técnico incluido"
                     className="h-11"
                   />
                 </div>
               </div>

               <div className="space-y-2">
                 <Label htmlFor="warranty_coverage" className="text-sm font-medium">
                   Cobertura de Garantía
                 </Label>
                 <Textarea
                   id="warranty_coverage"
                   value={data.product.warranty_coverage}
                   onChange={(e) =>
                     setData("product.warranty_coverage", e.target.value)
                   }
                   placeholder="Detalles de la cobertura de garantía..."
                   rows={3}
                   className="resize-none"
                 />
               </div>

               <div className="space-y-2">
                 <Label htmlFor="returns_policy" className="text-sm font-medium">
                   Política de Devoluciones
                 </Label>
                 <Textarea
                   id="returns_policy"
                   value={data.product.returns_policy}
                   onChange={(e) =>
                     setData("product.returns_policy", e.target.value)
                   }
                   placeholder="Detalles de la política de devoluciones..."
                   rows={3}
                   className="resize-none"
                 />
               </div>
             </CardContent>
           </Card>

           {/* Sección Exclusiva (condicional) */}
            {data.product.is_featured && (
             <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950">
               <CardHeader className="pb-6">
                 <CardTitle className="flex items-center gap-2 text-xl">
                   <Star className="h-6 w-6 text-yellow-600" />
                   Contenido Exclusivo
                 </CardTitle>
               </CardHeader>
               <CardContent className="space-y-8">
                 <div className="space-y-2">
                   <Label htmlFor="long_description" className="text-sm font-medium">
                     Descripción Extendida
                   </Label>
                   <Textarea
                    id="long_description"
                    value={data.product.long_description}
                    onChange={(e) =>
                      setData("product.long_description", e.target.value)
                    }
                    placeholder="Descripción detallada para productos exclusivos"
                    rows={3}
                       />
                         {exclusiveReviews.map((review, index) => (
                          <div key={index} className="space-y-4 p-4 border rounded-lg">
                            <Textarea
                              placeholder="Comentario de la reseña"
                              value={review.comment}
                              onChange={(e) => {
                                const newReviews = [...exclusiveReviews];
                                newReviews[index].comment = e.target.value;
                                setExclusiveReviews(newReviews);
                              }}
                              rows={2}
                            />
                            <div className="grid grid-cols-2 gap-4">
                              <Input
                                placeholder="Nombre de la persona"
                                value={review.person}
                                onChange={(e) => {
                                  const newReviews = [...exclusiveReviews];
                                  newReviews[index].person = e.target.value;
                                  setExclusiveReviews(newReviews);
                                }}
                              />
                              <Input
                                placeholder="Profesión"
                                value={review.profession}
                                onChange={(e) => {
                                  const newReviews = [...exclusiveReviews];
                                  newReviews[index].profession = e.target.value;
                                  setExclusiveReviews(newReviews);
                                }}
                              />
                            </div>
                            {exclusiveReviews.length > 1 && (
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() => setExclusiveReviews(exclusiveReviews.filter((_, i) => i !== index))}
                              >
                                Eliminar Reseña
                              </Button>
                            )}
                          </div>
                        ))}
                   <Button
                     type="button"
                     variant="outline"
                     onClick={() => setExclusiveReviews([...exclusiveReviews, { comment: '', person: '', profession: '' }])}
                   >
                     Agregar Reseña
                    </Button>
                  </div>

                  {/* Preguntas Frecuentes */}
                 <div className="space-y-4">
                   <Label className="text-sm font-medium">Preguntas Frecuentes</Label>
                   {faqs.map((faq, index) => (
                     <div key={index} className="p-4 border rounded-lg space-y-3">
                       <Input
                         placeholder="Pregunta"
                         value={faq.question}
                         onChange={(e) => {
                           const newFaqs = [...faqs];
                           newFaqs[index].question = e.target.value;
                           setFaqs(newFaqs);
                         }}
                       />
                       <Textarea
                         placeholder="Respuesta"
                         value={faq.answer}
                         onChange={(e) => {
                           const newFaqs = [...faqs];
                           newFaqs[index].answer = e.target.value;
                           setFaqs(newFaqs);
                         }}
                         rows={2}
                       />
                       {faqs.length > 3 && (
                         <Button
                           type="button"
                           variant="destructive"
                           size="sm"
                           onClick={() => setFaqs(faqs.filter((_, i) => i !== index))}
                         >
                           Eliminar FAQ
                         </Button>
                       )}
                     </div>
                   ))}
                   {faqs.length < 4 && (
                     <Button
                       type="button"
                       variant="outline"
                       onClick={() => setFaqs([...faqs, { question: '', answer: '' }])}
                     >
                       Agregar FAQ
                     </Button>
                   )}
                 </div>
               </CardContent>
              </Card>
            )}

          <DialogFooter className="gap-4 pt-8 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={processing || isCompressing}
              className="flex-1 lg:flex-none lg:min-w-32"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={processing || isCompressing}
              className="flex-1 lg:flex-none lg:min-w-48 bg-slate-900 hover:bg-slate-800 text-white"
            >
              <Package className="h-5 w-5 mr-2" />
              {processing
                ? "Guardando..."
                : isCompressing
                  ? "Procesando imágenes..."
                : product
                  ? "Actualizar Producto"
                  : "Crear Producto"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { ProductFormModal };
